// src/components/forms/ExamSessionForm.jsx
import { useEffect, useState } from 'react';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';

const defaultForm = {
  name: '',
  start_date: '',
  end_date: '',
  notes: '',
};

function validate(form) {
  const errors = {};
  if (!form.name?.trim()) errors.name = 'Name is required';
  if (!form.start_date) errors.start_date = 'Start date is required';
  if (!form.end_date) errors.end_date = 'End date is required';

  if (form.start_date && form.end_date) {
    const s = new Date(form.start_date);
    const e = new Date(form.end_date);
    if (s > e) errors.end_date = 'End date must be on/after start date';
  }
  return errors;
}

export default function ExamSessionForm({
  initialData,
  onSubmit,
  onCancel,
  loading = false,
}) {
  const [form, setForm] = useState(defaultForm);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setForm({
        name: initialData.name ?? '',
        start_date: initialData.start_date ?? '',
        end_date: initialData.end_date ?? '',
        notes: initialData.notes ?? '',
      });
    } else {
      setForm(defaultForm);
    }
    setErrors({});
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = validate(form);
    setErrors(v);
    if (Object.keys(v).length) return;

    // Pass minimal payload the hook expects
    await onSubmit({
      name: form.name.trim(),
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      notes: form.notes?.trim() || null,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Session Name
        </label>
        <Input
          name="name"
          value={form.name}
          onChange={handleChange}
          placeholder='e.g. "August 2025"'
          disabled={loading}
        />
        {errors.name && (
          <p className="mt-1 text-sm text-red-600">{errors.name}</p>
        )}
      </div>

      {/* Dates */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Start Date
          </label>
          <Input
            type="date"
            name="start_date"
            value={form.start_date ?? ''}
            onChange={handleChange}
            disabled={loading}
          />
          {errors.start_date && (
            <p className="mt-1 text-sm text-red-600">{errors.start_date}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            End Date
          </label>
          <Input
            type="date"
            name="end_date"
            value={form.end_date ?? ''}
            onChange={handleChange}
            disabled={loading}
          />
          {errors.end_date && (
            <p className="mt-1 text-sm text-red-600">{errors.end_date}</p>
          )}
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Notes (optional)
        </label>
        {/* Using a native textarea to avoid depending on a custom Textarea component */}
        <textarea
          name="notes"
          value={form.notes ?? ''}
          onChange={handleChange}
          rows={4}
          disabled={loading}
          className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="Remarks about this exam window…"
        />
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end space-x-3">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  );
}
